/**
 * Document Processing Service
 * 
 * Handles document upload, chunking, embedding, and vector storage.
 * Supports PDF, DOCX, TXT, MD file types.
 * Uses OpenAI embeddings (text-embedding-3-large) or PubMedBERT.
 * Supports Firestore Vector Search extension.
 */

import { storage, db, firebaseApp } from '@healthdesk/shared-services';
import firebase from 'firebase/compat/app';
import { debugLog, debugDebug } from '../../utils/debugLog';
import { detectPHI } from './phiDetectionService';
import { chunkAtMedicalBoundaries, validateMedicalTerminology } from './medicalTerminologyService';

// File size limits (generous)
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB per file (ZIP/PDF/DOCX — large ZIPs for bulk upload)
const MAX_TOTAL_SIZE = 250 * 1024 * 1024; // 250MB total per position
const MAX_DOCUMENTS = 200; // Max documents per position (supports bulk ZIP + many single uploads)

// Chunking configuration for medical documents (structure-aware; see medicalTerminologyService.js).
// Clinical RAG research (MDPI Bioengineering 2025, etc.) recommends aligning to logical/section
// boundaries and ~500-word (~2500 char) chunks for adaptive chunking; we pass this to boundary-based
// chunking; structured (algorithm) text uses a larger cap to keep sections intact.
const MEDICAL_CHUNK_SIZE_CHARS = 2500;   // ~500 words; improves precision for dense clinical text
const MEDICAL_CHUNK_OVERLAP_CHARS = 400; // ~10–15% overlap for context continuity

// Max text length per embedding call (must match generateEmbedding Cloud Function limit; OpenAI 8192 tokens ≈ 32k chars)
const MAX_EMBEDDING_TEXT_LENGTH = 30000;

// Cloud Function timeout for heavy file extraction (PDF/DOCX/ZIP; match functions timeoutSeconds: 300)
const CLOUD_FUNCTION_TIMEOUT_MS = 300_000;

// Firestore batch: max 500 ops per batch, but request payload must stay under ~10MB (chunks include embeddings).
// Keep batch size conservative so one commit doesn't exceed the limit.
const FIRESTORE_BATCH_SIZE = 200;

/**
 * Extract text from different file types
 * Uses Cloud Function for complex file types (PDF, DOCX)
 * Returns both raw and structured text (structure-preserved via LLM for clinical algorithms)
 * @param {File} file - File object
 * @returns {Promise<{text: string, structuredText: string|null, structurePreserved: boolean}>} Extraction result
 */
const extractTextFromFile = async (file) => {
  const fileType = file.type || file.name.split('.').pop().toLowerCase();
  
  // Simple text files - extract client-side (no structure preservation needed)
  if (fileType === 'text/plain' || fileType === 'txt' || fileType === 'md' || fileType === 'markdown') {
    const text = await file.text();
    return { text, structuredText: null, structurePreserved: false };
  }
  
  // Complex files (PDF, DOCX, ZIP) - use Cloud Function with structure preservation
  if (fileType === 'application/pdf' || fileType === 'pdf' || 
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
      fileType === 'docx' ||
      fileType === 'application/zip' || fileType === 'application/x-zip-compressed' || fileType === 'zip') {
    // Upload to temporary storage and call Cloud Function
    const tempRef = storage.ref(`temp/${Date.now()}_${file.name}`);
    await tempRef.put(file);
    const tempURL = await tempRef.getDownloadURL();
    
    const extractTextFunction = firebaseApp.functions().httpsCallable('extractTextFromFile', { timeout: CLOUD_FUNCTION_TIMEOUT_MS });
    const result = await extractTextFunction({ 
      fileURL: tempURL, 
      fileType,
      preserveStructure: true, // Request LLM structure preservation
    });
    
    // Clean up temp file
    await tempRef.delete();
    
    let text;
    let structuredText;
    let structurePreserved;
    let usedMarker = false;
    if (result.data.extractedContentURL) {
      // Large response was written to Storage; fetch from URL
      const res = await fetch(result.data.extractedContentURL);
      if (!res.ok) throw new Error(`Failed to fetch extracted content: ${res.status}`);
      const payload = await res.json();
      text = payload.text ?? '';
      structuredText = payload.structuredText ?? null;
      structurePreserved = !!payload.structurePreserved;
      usedMarker = !!payload.usedMarker;
    } else {
      text = result.data.text ?? '';
      structuredText = result.data.structuredText ?? null;
      structurePreserved = !!result.data.structurePreserved;
      usedMarker = !!result.data.usedMarker;
    }
    // Temporary: report once per document in browser console (remove after confirming)
    if (usedMarker) {
      console.log('[DocumentProcessing] Marker Service is being used');
    }
    if (structurePreserved) {
      debugLog('[DocumentProcessing] Structure preserved by LLM:', {
        rawLength: text.length,
        structuredLength: structuredText?.length,
      });
    }
    
    return { 
      text, 
      structuredText: structuredText || null, 
      structurePreserved: !!structurePreserved,
    };
  }
  
  throw new Error(`Unsupported file type: ${fileType}. Supported: TXT, MD, PDF, DOCX, ZIP`);
};

/**
 * Extract section titles from document text
 * @param {string} text - Document text
 * @returns {Array<{title: string, position: number}>} Section titles with positions
 */
const extractSectionTitles = (text) => {
  const sectionTitles = [];
  const sectionPatterns = [
    /(?:^|\n)\s*(#{1,3})\s+(.+?)(?:\n|$)/g, // Markdown headers
    /(?:^|\n)\s*([A-Z][A-Z\s]{2,50}):\s*(?:\n|$)/g, // ALL CAPS headers
    /(?:^|\n)\s*\d+\.\s*([A-Z][^\n]{2,50})(?:\n|$)/g, // Numbered sections
  ];

  sectionPatterns.forEach(pattern => {
    const matches = [...text.matchAll(pattern)];
    matches.forEach(match => {
      const position = match.index || 0;
      const title = (match[1] || match[2] || '').trim();
      if (title && title.length > 2 && title.length < 100) {
        sectionTitles.push({ title, position });
      }
    });
  });

  // Sort by position
  return sectionTitles.sort((a, b) => a.position - b.position);
};

/**
 * Find the section title for a given chunk
 * @param {Object} chunk - Chunk object with start/end positions
 * @param {Array<{title: string, position: number}>} sectionTitles - Array of section titles
 * @returns {string|null} Section title or null
 */
const findSectionTitleForChunk = (chunk, sectionTitles) => {
  // Find the most recent section title before this chunk
  for (let i = sectionTitles.length - 1; i >= 0; i--) {
    if (sectionTitles[i].position <= chunk.start) {
      return sectionTitles[i].title;
    }
  }
  return null;
};

/**
 * Chunk text at medical concept boundaries (medical-aware chunking)
 * @param {string} text - Text to chunk
 * @param {number} chunkSize - Target chunk size in characters
 * @param {number} overlap - Overlap size in characters
 * @returns {Array<{text: string, index: number, start: number, end: number, conceptType?: string}>} Chunks
 */
const chunkText = (text, chunkSize = MEDICAL_CHUNK_SIZE_CHARS, overlap = MEDICAL_CHUNK_OVERLAP_CHARS) => {
  // Use medical-aware, structure-aware chunking (see medicalTerminologyService.js)
  return chunkAtMedicalBoundaries(text, chunkSize, overlap);
};

/**
 * Generate embeddings using OpenAI via Cloud Function
 * @param {string} text - Text to embed
 * @param {string} model - Embedding model ('text-embedding-3-large' or 'pubmedbert')
 * @returns {Promise<Array<number>>} Embedding vector
 */
const generateEmbedding = async (text, model = 'text-embedding-3-large') => {
  // Call Firebase Cloud Function for secure embedding generation
  // Supports text-embedding-3-large (1536 dims) or PubMedBERT (768 dims)
  const generateEmbeddingFunction = firebaseApp.functions().httpsCallable('generateEmbedding');
  const result = await generateEmbeddingFunction({ text, model });
  return result.data.embedding;
};

/**
 * Extract metadata from file
 * @param {File} file - File object
 * @param {string} text - Extracted text
 * @param {Object} phiDetection - PHI detection results
 * @param {Object} medicalValidation - Medical terminology validation results
 * @returns {Object} Metadata
 */
const extractMetadata = (file, text, phiDetection, medicalValidation) => {
  const metadata = {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type || file.name.split('.').pop().toLowerCase(),
    uploadedAt: new Date().toISOString(),
    characterCount: text.length,
    wordCount: text.split(/\s+/).filter(Boolean).length,
    estimatedTokens: Math.ceil(text.length / 4), // Rough estimate: 1 token ≈ 4 characters
    
    // PHI detection metadata
    containsPHI: phiDetection.hasPHI,
    phiTypes: phiDetection.phiTypes,
    phiCount: phiDetection.locations.length,
    
    // Medical terminology metadata
    medicalTermCount: medicalValidation.medicalTermCount,
    foundTerms: medicalValidation.foundTerms,
    medicalValidationIssues: medicalValidation.issues,
    
    // Source metadata (required fields - will be filled by user)
    source: null, // Document source (e.g., "ACOG Guidelines", "UpToDate")
    author: null, // Author or organization
    publicationDate: null, // Publication date
    version: null, // Version number
    evidenceLevel: null, // Evidence level (RCT, meta-analysis, guideline, etc.)
    specialty: null, // Medical specialty
    documentType: null, // Type: guideline, protocol, reference, etc.
    classification: phiDetection.hasPHI ? 'phi' : 'general', // 'phi' or 'general'
  };
  
  return metadata;
};

class DocumentProcessingService {
  constructor() {
    this.storageRef = storage.ref('llm-documents');
    this.collection = 'llmDocumentVectors';
  }

  /**
   * Check for duplicate documents
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {string} fileName - File name
   * @param {number} fileSize - File size
   * @param {Object} options - { adminId } for Position 3
   * @returns {Promise<Object|null>} Duplicate document if found
   */
  async checkForDuplicate(baseQueryId, position, fileName, fileSize, options = {}) {
    const documents = await this.getDocumentsForPosition(baseQueryId, position, options);
    
    // Check for exact filename match
    const filenameMatch = documents.find(doc => 
      doc.fileName.toLowerCase() === fileName.toLowerCase()
    );
    if (filenameMatch) {
      return { type: 'filename', document: filenameMatch };
    }
    
    // Check for similar file size (within 1KB - likely same file)
    const sizeMatch = documents.find(doc => 
      Math.abs(doc.metadata?.fileSize - fileSize) < 1024
    );
    if (sizeMatch) {
      return { type: 'size', document: sizeMatch };
    }
    
    return null;
  }

  /**
   * Process and upload a document
   * @param {File} file - File to process
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position (1, 2, or 3)
   * @param {string} userId - User ID
   * @param {Object} options - { adminId, sourceMetadata, redactPHI, onProgress } for Position 3; onProgress(percent, statusMessage?) for UI
   * @returns {Promise<Object>} Document metadata
   */
  async processDocument(file, baseQueryId, position, userId, options = {}) {
    const onProgress = options.onProgress && typeof options.onProgress === 'function' ? options.onProgress : () => {};
    const report = (percent, status) => {
      onProgress(percent, status);
    };
    console.log('[embed] processDocument: start', file.name);
    debugLog('[DocumentProcessing] processDocument called:', { baseQueryId, position, userId, adminId: options.adminId });
    // Validate file
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File size exceeds limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }
    console.log('[embed] processDocument: file size OK');

    report(2, 'Checking for duplicates…');
    // Check for duplicates
    console.log('[embed] processDocument: duplicate check...');
    debugLog('[DocumentProcessing] Step 1: checkForDuplicate');
    const duplicate = await this.checkForDuplicate(baseQueryId, position, file.name, file.size, options);
    console.log('[embed] processDocument: duplicate check done');
    if (duplicate && !options.allowDuplicate) {
      throw new Error(`Duplicate document detected: ${duplicate.document.fileName}. Use "Replace" to update existing document.`);
    }

    // Check total size for position
    console.log('[embed] processDocument: total size check...');
    debugLog('[DocumentProcessing] Step 2: getTotalSizeForPosition');
    const currentSize = await this.getTotalSizeForPosition(baseQueryId, position, options);
    if (currentSize + file.size > MAX_TOTAL_SIZE) {
      throw new Error(`Total size for Position ${position} would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB`);
    }
    console.log('[embed] processDocument: total size check done');

    // Check document count
    console.log('[embed] processDocument: doc count check...');
    debugLog('[DocumentProcessing] Step 3: getDocumentCountForPosition');
    const currentCount = await this.getDocumentCountForPosition(baseQueryId, position, options);
    if (currentCount >= MAX_DOCUMENTS) {
      throw new Error(`Maximum ${MAX_DOCUMENTS} documents per position`);
    }
    console.log('[embed] processDocument: doc count check done');

    report(5, 'Extracting text from file…');
    // Extract text (with structure preservation for clinical algorithms)
    console.log('[embed] processDocument: calling extractTextFromFile');
    debugLog('[DocumentProcessing] Step 4: extractTextFromFile');
    const extraction = await extractTextFromFile(file);
    console.log('[embed] processDocument: extractTextFromFile done, text length', extraction.text?.length ?? 0);
    report(12, 'Text extracted. Chunking…');
    let text = extraction.text;
    const structuredText = extraction.structuredText;
    const structurePreserved = extraction.structurePreserved;
    
    debugLog('[DocumentProcessing] Step 4 result:', {
      extractedTextLength: text.length,
      extractedTextKB: (text.length / 1024).toFixed(1) + ' KB',
      fileSizeMB: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      textToFileRatio: ((text.length / file.size) * 100).toFixed(1) + '%',
      structurePreserved,
      structuredTextLength: structuredText?.length || 0,
    });
    
    // Use structured text for chunking/embedding if available
    // (preserves algorithm decision trees, scoring criteria, treatment pathways)
    const textForChunking = structuredText || text;
    
    // Detect PHI on raw text (more reliable than structured)
    const phiDetection = detectPHI(text);
    
    // Redact PHI if requested
    if (options.redactPHI && phiDetection.hasPHI) {
      text = phiDetection.redactedText;
    }
    
    // Validate medical terminology on raw text
    const medicalValidation = validateMedicalTerminology(text);
    
    // Extract metadata (includes PHI and medical validation info)
    const metadata = extractMetadata(file, text, phiDetection, medicalValidation);
    metadata.structurePreserved = structurePreserved;
    
    // Merge user-provided source metadata
    if (options.sourceMetadata) {
      Object.assign(metadata, options.sourceMetadata);
    }
    
    // Only source is required; default publication date to today if missing
    if (!metadata.source || !String(metadata.source).trim()) {
      throw new Error('Required source metadata missing: source is required');
    }
    if (!metadata.publicationDate) {
      metadata.publicationDate = new Date().toISOString().split('T')[0];
    }
    
    report(15, 'Chunking…');
    // Chunk text using medical-aware chunking
    console.log('[embed] processDocument: chunking...');
    debugLog('[DocumentProcessing] Step 5: chunkText (using', structurePreserved ? 'structured' : 'raw', 'text)');
    const chunks = chunkText(textForChunking);
    console.log('[embed] processDocument: chunking done, chunks', chunks.length);
    debugLog('[DocumentProcessing] Step 5 result:', {
      chunkCount: chunks.length,
      avgChunkSize: chunks.length > 0 ? Math.round(chunks.reduce((sum, c) => sum + c.text.length, 0) / chunks.length) : 0,
      smallestChunk: chunks.length > 0 ? Math.min(...chunks.map(c => c.text.length)) : 0,
      largestChunk: chunks.length > 0 ? Math.max(...chunks.map(c => c.text.length)) : 0,
    });

    const totalChunks = chunks.length;
    report(20, `Generating embeddings (0 / ${totalChunks})…`);
    console.log('[embed] processDocument: generating embeddings, total chunks', totalChunks);
    // Extract section titles and structure from text for metadata-rich chunks
    const sectionTitles = extractSectionTitles(text);

    // Generate embeddings for each chunk with metadata (20% -> 75% of progress)
    const chunksWithEmbeddings = [];
    const embeddingModel = options.embeddingModel || 'text-embedding-3-large'; // Default to large model

    debugLog('[DocumentProcessing] Step 6: generateEmbeddings for', chunks.length, 'chunks');
    for (let i = 0; i < chunks.length; i++) {
      if (i === 0 || i === chunks.length - 1 || (i + 1) % 10 === 0) {
        console.log('[embed] processDocument: embedding chunk', i + 1, '/', totalChunks);
      }
      const chunk = chunks[i];
      const sectionTitle = findSectionTitleForChunk(chunk, sectionTitles);

      const textToEmbed = chunk.text.length > MAX_EMBEDDING_TEXT_LENGTH
        ? chunk.text.slice(0, MAX_EMBEDDING_TEXT_LENGTH)
        : chunk.text;
      const embedding = await generateEmbedding(textToEmbed, embeddingModel);

      chunksWithEmbeddings.push({
        ...chunk,
        embedding,
        sectionTitle: sectionTitle || null,
        documentType: metadata.documentType || null,
        specialty: metadata.specialty || null,
        source: metadata.source || null,
        author: metadata.author || null,
        publicationDate: metadata.publicationDate || null,
        baseQueryId,
        position,
        adminId: options.adminId || null,
      });
      const embedPercent = 20 + Math.round((55 * (i + 1)) / totalChunks);
      report(embedPercent, `Embedding chunk ${i + 1} / ${totalChunks}…`);
    }
    console.log('[embed] processDocument: embeddings done');

    report(78, 'Uploading to storage…');
    // Upload file to Firebase Storage
    console.log('[embed] processDocument: uploading to Storage');
    debugLog('[DocumentProcessing] Step 7: upload to Storage');
    const filePath = `${baseQueryId}/${position}/${options.adminId || 'global'}/${Date.now()}_${file.name}`;
    const fileRef = this.storageRef.child(filePath);
    await fileRef.put(file);
    console.log('[embed] processDocument: Storage upload done');
    report(88, 'Saving to database…');
    const downloadURL = await fileRef.getDownloadURL();
    
    // Calculate staleness (months since publication)
    const publicationDate = metadata.publicationDate ? new Date(metadata.publicationDate) : new Date();
    const monthsSincePublication = Math.floor((new Date() - publicationDate) / (1000 * 60 * 60 * 24 * 30));
    const isStale = monthsSincePublication > 6; // Stale if > 6 months old
    
    // Store document metadata and vectors in Firestore
    console.log('[embed] processDocument: writing Firestore doc');
    debugLog('[DocumentProcessing] Step 8: create Firestore doc', { position, adminId: options.adminId, userId });
    const docRef = db.collection(this.collection).doc();
    const documentData = {
      id: docRef.id,
      baseQueryId,
      position,
      adminId: options.adminId || null,
      userId,
      fileName: file.name,
      filePath,
      downloadURL,
      metadata: {
        ...metadata,
        monthsSincePublication,
        isStale,
        stalenessWarning: isStale ? `Document is ${monthsSincePublication} months old. Consider updating.` : null,
      },
      chunkCount: chunksWithEmbeddings.length,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    
    debugLog('[DocumentProcessing] Step 8a: docRef.set');
    await docRef.set(documentData);
    console.log('[embed] processDocument: Firestore doc done');

    debugLog('[DocumentProcessing] Step 9: store chunks in subcollection');
    // Store chunks with embeddings in subcollection (Firestore batch limit 500; split for large ZIPs)
    const chunksRef = docRef.collection('chunks');
    for (let offset = 0; offset < chunksWithEmbeddings.length; offset += FIRESTORE_BATCH_SIZE) {
      console.log('[embed] processDocument: writing chunks batch, offset', offset);
      const batch = db.batch();
      const slice = chunksWithEmbeddings.slice(offset, offset + FIRESTORE_BATCH_SIZE);
      slice.forEach((chunk, i) => {
        const index = offset + i;
        const chunkRef = chunksRef.doc(`${index}`);
        batch.set(chunkRef, {
          text: chunk.text,
          embedding: chunk.embedding,
          index: chunk.index,
          start: chunk.start,
          end: chunk.end,
          conceptType: chunk.conceptType || null,
          sectionTitle: chunk.sectionTitle || null,
          documentType: chunk.documentType || null,
          specialty: chunk.specialty || null,
          source: chunk.source || null,
          author: chunk.author || null,
          publicationDate: chunk.publicationDate || null,
          baseQueryId: chunk.baseQueryId,
          position: chunk.position,
          adminId: chunk.adminId,
          documentId: docRef.id,
        });
      });
      await batch.commit();
      console.log('[embed] processDocument: chunks batch done, offset', offset);
    }
    report(100, 'Done');
    console.log('[embed] processDocument: done', file.name);

    return {
      id: docRef.id,
      ...documentData,
    };
  }

  /**
   * Get a document by ID
   * @param {string} documentId - Document ID
   * @returns {Promise<Object|null>} Document metadata or null
   */
  async getDocumentById(documentId) {
    try {
      const doc = await db.collection(this.collection).doc(documentId).get();
      if (!doc.exists) return null;
      
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null,
      };
    } catch (error) {
      console.error('[DocumentProcessing] Failed to get document:', error);
      return null;
    }
  }

  /**
   * Get documents for a position
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Object} options - { adminId } for Position 3
   * @returns {Promise<Array>} Array of document metadata
   */
  async getDocumentsForPosition(baseQueryId, position, options = {}) {
    let query = db.collection(this.collection)
      .where('baseQueryId', '==', baseQueryId)
      .where('position', '==', position);
    
    if (position === 3 && options.adminId) {
      query = query.where('adminId', '==', options.adminId);
    } else if (position === 3) {
      query = query.where('adminId', '==', null);
    }
    
    const snapshot = await query.orderBy('createdAt', 'desc').get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null,
    }));
  }

  /**
   * Delete a document
   * @param {string} documentId - Document ID
   * @returns {Promise<void>}
   */
  async deleteDocument(documentId) {
    const docRef = db.collection(this.collection).doc(documentId);
    const doc = await docRef.get();
    
    if (!doc.exists) {
      throw new Error('Document not found');
    }
    
    const data = doc.data();
    
    // Delete file from Storage
    if (data.filePath) {
      const fileRef = this.storageRef.child(data.filePath);
      await fileRef.delete();
    }
    
    // Delete chunks subcollection
    const chunksRef = docRef.collection('chunks');
    const chunksSnapshot = await chunksRef.get();
    const batch = db.batch();
    chunksSnapshot.docs.forEach(chunkDoc => {
      batch.delete(chunkDoc.ref);
    });
    await batch.commit();
    
    // Delete document
    await docRef.delete();
  }

  /**
   * Get total size for a position
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Object} options - { adminId } for Position 3
   * @returns {Promise<number>} Total size in bytes
   */
  async getTotalSizeForPosition(baseQueryId, position, options = {}) {
    const documents = await this.getDocumentsForPosition(baseQueryId, position, options);
    return documents.reduce((sum, doc) => sum + (doc.metadata?.fileSize || 0), 0);
  }

  /**
   * Get document count for a position
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Object} options - { adminId } for Position 3
   * @returns {Promise<number>} Document count
   */
  async getDocumentCountForPosition(baseQueryId, position, options = {}) {
    const documents = await this.getDocumentsForPosition(baseQueryId, position, options);
    return documents.length;
  }

  /**
   * Search for relevant chunks using Firestore Vector Search
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Array<number>} queryEmbedding - Query embedding vector
   * @param {number} topK - Number of chunks to return
   * @param {Object} options - { adminId, specialty, documentType, useVectorSearch } for filtering
   * @returns {Promise<Array>} Array of relevant chunks with similarity scores
   */
  async searchSimilarChunks(baseQueryId, position, queryEmbedding, topK = 10, options = {}) {
    // Get all documents for position (for metadata)
    const documents = await this.getDocumentsForPosition(baseQueryId, position, options);
    if (documents.length === 0) {
      return [];
    }

    // Build metadata filters for Firestore vector search
    const metadataFilters = {
      baseQueryId,
      position,
    };
    
    if (position === 3 && options.adminId) {
      metadataFilters.adminId = options.adminId;
    } else if (position === 3) {
      metadataFilters.adminId = null;
    }
    
    if (options.specialty) {
      metadataFilters.specialty = options.specialty;
    }
    
    if (options.documentType) {
      metadataFilters.documentType = options.documentType;
    }

    // Use Firestore Vector Search for each document's chunks
    // Note: Firestore vector search requires an index to be created in Firebase Console
    // Collection path: llmDocumentVectors/{docId}/chunks
    // Vector field: embedding
    // Distance metric: COSINE
    
    const allChunks = [];
    
    for (const doc of documents) {
      try {
        const chunksRef = db.collection(this.collection).doc(doc.id).collection('chunks');
        
        // Firestore Vector Search query
        // Note: This requires the vector search index to be created
        // The query uses findNearest() method which is available when vector search is enabled
        let query = chunksRef
          .where('baseQueryId', '==', baseQueryId)
          .where('position', '==', position);
        
        if (metadataFilters.adminId !== undefined) {
          query = query.where('adminId', '==', metadataFilters.adminId);
        }
        
        // Try Firestore Vector Search first (if index exists)
        const useVectorSearch = options.useVectorSearch !== false; // Default to true, allow override
        let usedVectorSearch = false;
        
        if (useVectorSearch && typeof query.findNearest === 'function') {
          try {
            const vectorSearchResults = await query
              .findNearest('embedding', queryEmbedding, {
                limit: topK * 2, // Get more candidates for per-document search
                distanceMeasure: 'COSINE',
              })
              .get();
            
            vectorSearchResults.docs.forEach(chunkDoc => {
              const chunkData = chunkDoc.data();
              // Convert distance to similarity (distance is 0-2 for cosine, similarity is 1-distance/2)
              const distance = chunkDoc.distance || 0;
              const similarity = 1 - (distance / 2);
              
              allChunks.push({
                documentId: doc.id,
                documentName: doc.fileName,
                chunkId: chunkDoc.id,
                ...chunkData,
                similarity: Math.max(0, Math.min(1, similarity)), // Clamp to 0-1
              });
            });
            
            usedVectorSearch = true;
          } catch (vectorError) {
            // Vector search not available (index not created or method not available)
            // Fall through to client-side similarity
            debugDebug(`[DocumentProcessing] Vector search not available for document ${doc.id}, using fallback:`, vectorError.message);
          }
        }
        
        // Fallback to client-side similarity calculation
        if (!usedVectorSearch) {
          const chunksSnapshot = await query.get();
          
          chunksSnapshot.docs.forEach(chunkDoc => {
            const chunkData = chunkDoc.data();
            if (!chunkData.embedding || chunkData.embedding.length === 0) {
              return; // Skip chunks without embeddings
            }
            
            // Calculate similarity client-side
            const similarity = this.cosineSimilarity(queryEmbedding, chunkData.embedding);
            
            allChunks.push({
              documentId: doc.id,
              documentName: doc.fileName,
              chunkId: chunkDoc.id,
              ...chunkData,
              similarity,
            });
          });
        }
      } catch (error) {
        console.warn(`[DocumentProcessing] Error searching chunks for document ${doc.id}:`, error);
        // Continue with other documents
      }
    }
    
    // Sort by similarity and return top K
    return allChunks
      .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
      .slice(0, topK);
  }
  
  /**
   * Search using Firestore Vector Search (when index is available)
   * This is an alias for searchSimilarChunks with vector search enabled
   * @param {string} baseQueryId - Base query ID
   * @param {number} position - Chain position
   * @param {Array<number>} queryEmbedding - Query embedding vector
   * @param {number} topK - Number of chunks to return
   * @param {Object} options - { adminId, specialty, documentType } for filtering
   * @returns {Promise<Array>} Array of relevant chunks with similarity scores
   */
  async searchSimilarChunksWithVectorSearch(baseQueryId, position, queryEmbedding, topK = 10, options = {}) {
    // Delegate to searchSimilarChunks with vector search enabled
    return this.searchSimilarChunks(baseQueryId, position, queryEmbedding, topK, {
      ...options,
      useVectorSearch: true,
    });
  }

  /**
   * Calculate cosine similarity between two vectors
   * @param {Array<number>} vecA - Vector A
   * @param {Array<number>} vecB - Vector B
   * @returns {number} Similarity score (0-1)
   */
  cosineSimilarity(vecA, vecB) {
    if (vecA.length !== vecB.length) {
      throw new Error('Vectors must have same length');
    }
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }
    
    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
  }
}

// Export singleton instance
export const documentProcessingService = new DocumentProcessingService();
export default documentProcessingService;
